import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { normaliseInviteCode } from "@luckymenu/core";
import { useSwitchRestaurant } from "../provider";

/** Why an invite cannot be used, for a message on the join screen. */
export type JoinProblem =
	| "invalid"
	| "expired"
	| "revoked"
	| "used_up"
	| "email_mismatch"
	| "membership_disabled"
	| "rate_limited"
	| "network"
	| "failed";

const REASONS = new Set<string>([
	"invalid",
	"expired",
	"revoked",
	"used_up",
	"email_mismatch",
	"membership_disabled",
]);

export function joinProblemOf(error: unknown): JoinProblem {
	const code = errorCodeOf(error);
	if (code === "RATE_LIMITED") return "rate_limited";
	if (code === "NETWORK") return "network";
	const reason = errorDetailsOf(error).reason;
	if (typeof reason === "string" && REASONS.has(reason)) return reason as JoinProblem;
	return code === "NOT_FOUND" || code === "VALIDATION" ? "invalid" : "failed";
}

/**
 * The join screen: previews the invite for `code` (typed or from the link),
 * and accepts it. Accepting makes the new restaurant the active one.
 */
export function useJoin(code: string) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const switchRestaurant = useSwitchRestaurant();
	const normalised = normaliseInviteCode(code);
	const preview = useQuery({
		...trpc.staff.tenancy.previewInvite.queryOptions({ code: normalised ?? "" }),
		enabled: normalised !== null,
		retry: false,
		staleTime: 0,
	});
	const accept = useMutation(
		trpc.staff.tenancy.acceptInvite.mutationOptions({
			onSuccess: async (restaurant) => {
				await queryClient.invalidateQueries({
					queryKey: trpc.staff.tenancy.listMyRestaurants.queryKey(),
				});
				await switchRestaurant(restaurant.id);
			},
		}),
	);
	return {
		/** The code as stored, or null when it cannot be a code. */
		code: normalised,
		preview,
		previewProblem: preview.error ? joinProblemOf(preview.error) : null,
		accept: () => (normalised ? accept.mutateAsync({ code: normalised }) : Promise.reject()),
		accepting: accept.isPending,
		acceptProblem: accept.error ? joinProblemOf(accept.error) : null,
		joined: accept.data ?? null,
	};
}
